(() => {
  if (!customElements.get('origen-product-form')) {
    class OrigenProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form || this.initialized) return;
        this.initialized = true;
        this.form.noValidate = true;
        this.select = this.form.querySelector('select[name="id"]');
        this.quantity = this.form.querySelector('input[name="quantity"]');
        this.submitButton = this.form.querySelector('[data-submit]');
        this.priceEl = this.form.querySelector('[data-price]');
        this.statusEl = this.form.querySelector('[role="status"]');
        const variantsData = this.form.querySelector('[data-variants]');
        this.variants = variantsData ? JSON.parse(variantsData.textContent) : [];

        this.select.addEventListener('change', () => {
          this.select.removeAttribute('aria-invalid');
          this.setStatus('');
          this.render();
          if (this.hasAttribute('data-update-url')) {
            const url = new URL(window.location.href);
            if (this.select.value) {
              url.searchParams.set('variant', this.select.value);
            } else {
              url.searchParams.delete('variant');
            }
            window.history.replaceState({}, '', url);
          }
        });
        this.form.querySelectorAll('[data-step]').forEach((button) => {
          button.addEventListener('click', () => this.step(Number(button.dataset.step)));
        });
        this.quantity.addEventListener('change', () => this.step(0));
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.render();
      }

      get selectedVariant() {
        return this.variants.find((variant) => String(variant.id) === this.select.value);
      }

      setStatus(message) {
        this.statusEl.textContent = message;
      }

      step(delta) {
        const max = Number(this.quantity.max) || 10;
        const value = (Math.round(Number(this.quantity.value)) || 1) + delta;
        this.quantity.value = Math.min(max, Math.max(1, value));
      }

      render() {
        const variant = this.selectedVariant;
        const anyAvailable = this.variants.some((item) => item.available);
        if (!anyAvailable) {
          this.submitButton.disabled = true;
          this.submitButton.textContent = this.dataset.labelSoldOut;
        } else if (!variant) {
          this.submitButton.disabled = false;
          this.submitButton.textContent = this.dataset.labelChoose;
        } else if (!variant.available) {
          this.submitButton.disabled = true;
          this.submitButton.textContent = this.dataset.labelSoldOut;
        } else {
          this.submitButton.disabled = false;
          this.submitButton.textContent = this.dataset.labelAdd;
        }
        if (variant) {
          const current = document.createElement('span');
          current.textContent = variant.price;
          const parts = [current];
          if (variant.compare_at_price) {
            const regular = document.createElement('s');
            regular.textContent = variant.compare_at_price;
            parts.push(' ', regular);
          }
          this.priceEl.replaceChildren(...parts);
        }
      }

      markUnavailable(variantId) {
        const variant = this.variants.find((item) => String(item.id) === variantId);
        if (variant) variant.available = false;
        const option = this.select.querySelector(`option[value="${variantId}"]`);
        if (option && !option.disabled) {
          option.disabled = true;
          option.textContent = `${option.textContent} — ${this.dataset.labelSoldOut}`;
        }
        this.select.setAttribute('aria-invalid', 'true');
        this.setStatus(this.dataset.messageUnavailable);
      }

      async announceCart(variantId) {
        try {
          const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
          if (!response.ok) return;
          const cart = await response.json();
          this.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: this.form.id,
                data: { source: 'origen-product-form', itemCount: cart.item_count, variantId },
              },
            })
          );
        } catch (error) {
          return;
        }
      }

      async onSubmit(event) {
        event.preventDefault();
        this.setStatus('');
        const variantId = this.select.value;

        if (!this.selectedVariant) {
          this.select.setAttribute('aria-invalid', 'true');
          this.setStatus(`${this.dataset.labelChoose}.`);
          this.select.focus();
          return;
        }

        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-busy', 'true');

        try {
          const productResponse = await fetch(`${this.dataset.productUrl}.js`, {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
          });
          if (productResponse.ok) {
            const product = await productResponse.json();
            const live = product.variants.find((item) => String(item.id) === variantId);
            if (!live || !live.available) {
              this.markUnavailable(variantId);
              return;
            }
          }

          const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(this.form),
          });

          if (!response.ok) {
            this.markUnavailable(variantId);
            return;
          }

          const item = await response.json();
          if (this.dataset.afterAdd === 'cart') {
            window.location.assign(this.dataset.cartUrl);
            return;
          }
          this.setStatus(this.dataset.messageAdded);
          document.dispatchEvent(new CustomEvent('origen:cart-added', { bubbles: true, detail: { item } }));
          this.announceCart(variantId);
        } catch (error) {
          this.form.submit();
        } finally {
          this.submitButton.removeAttribute('aria-busy');
          this.render();
        }
      }
    }
    customElements.define('origen-product-form', OrigenProductForm);
  }

  if (!customElements.get('origen-carousel')) {
    class OrigenCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.current = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
        this.querySelectorAll('[data-direction]').forEach((button) => {
          button.addEventListener('click', () => this.go(Number(button.dataset.direction)));
        });
        this.addEventListener('shopify:block:select', (event) => {
          const index = this.slides.findIndex((slide) => slide.contains(event.target));
          if (index >= 0) this.show(index);
        });
      }

      go(direction) {
        this.show((this.current + direction + this.slides.length) % this.slides.length);
      }

      show(index) {
        this.slides[this.current].hidden = true;
        this.current = index;
        this.slides[this.current].hidden = false;
      }
    }
    customElements.define('origen-carousel', OrigenCarousel);
  }

  if (!customElements.get('origen-background-video')) {
    class OrigenBackgroundVideo extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.video = this.querySelector('video');
        this.toggle = this.querySelector('[data-video-toggle]');
        this.toggleLabel = this.querySelector('[data-video-toggle-label]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.userPaused = this.reducedMotion.matches;
        this.visible = false;

        this.toggle.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.sync();
        });

        if (!('IntersectionObserver' in window)) {
          this.visible = true;
          this.sync();
          return;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries.some((entry) => entry.isIntersecting);
            this.sync();
          },
          { rootMargin: '200px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      load() {
        if (this.loaded) return;
        this.loaded = true;
        const mobile = window.matchMedia('(max-width: 759px)').matches;
        this.video.src = mobile ? this.dataset.srcMobile : this.dataset.srcDesktop;
        this.toggle.hidden = false;
      }

      sync() {
        if (this.visible) this.load();
        const shouldPlay = this.visible && !this.userPaused;
        if (shouldPlay) {
          const attempt = this.video.play();
          if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {
            this.userPaused = true;
            this.setPaused(true);
          });
        } else if (this.loaded) {
          this.video.pause();
        }
        this.setPaused(this.userPaused);
      }

      setPaused(paused) {
        this.toggleAttribute('data-paused', paused);
        const label = paused ? this.dataset.labelPlay : this.dataset.labelPause;
        this.toggleLabel.textContent = label;
        this.toggle.setAttribute('aria-label', label);
      }
    }
    customElements.define('origen-background-video', OrigenBackgroundVideo);
  }

  if (!customElements.get('origen-newsletter')) {
    class OrigenNewsletter extends HTMLElement {
      connectedCallback() {
        const form = this.querySelector('form');
        if (!form || this.initialized) return;
        this.initialized = true;
        form.noValidate = true;
        const input = form.querySelector('input[type="email"]');
        const status = form.querySelector('[data-status]');
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

        form.addEventListener('submit', (event) => {
          const email = input.value.trim();
          if (!pattern.test(email)) {
            event.preventDefault();
            input.setAttribute('aria-invalid', 'true');
            status.textContent = this.dataset.messageInvalid;
            input.focus();
            return;
          }
          input.value = email;
          input.removeAttribute('aria-invalid');
        });

        input.addEventListener('input', () => {
          if (input.hasAttribute('aria-invalid')) {
            input.removeAttribute('aria-invalid');
            status.textContent = '';
          }
        });
      }
    }
    customElements.define('origen-newsletter', OrigenNewsletter);
  }
})();
